/**
 * Clinical Supervisor — per-therapist assessment grants panel.
 *
 * Lists the therapist's service-group assessment library. Items in the
 * group's defaults are granted automatically; supervisors can add
 * assessments beyond the default ('Grant') OR pull a specific therapist
 * back from a default ('Restrict'). Effective set the resolver applies
 * to the therapist is (defaults ∪ grants) − restrictions.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Checkbox, FormControlLabel, Button, Alert,
  CircularProgress, Divider, Chip, Snackbar, Tooltip
} from '@mui/material';
import api from '../../services/api';

export default function TherapistAssessmentGrants({ therapistId, token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [data, setData] = useState(null);
  const [grants, setGrants] = useState([]);
  const [restrictions, setRestrictions] = useState([]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/assessment-governance/supervisor/therapists/${therapistId}/assessment-grants`, token);
      setData(res?.data || null);
      setGrants(res?.data?.grants || []);
      setRestrictions(res?.data?.restrictions || []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load grants');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (therapistId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [therapistId, token]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(
        `/assessment-governance/supervisor/therapists/${therapistId}/assessment-grants`,
        { grants, restrictions },
        token
      );
      setToast('Assessment grants saved');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (!data) return null;

  const library = data.library || [];
  const defaults = new Set(data.defaults || []);
  const catalogue = data.catalogue || [];
  const grantedSet = new Set(grants);
  const restrictionSet = new Set(restrictions);

  // Toggle the effective availability of a given testKey.
  // Three buckets to manage:
  //   default + not-restricted  ← ticked, locked-effect = unticking adds a restriction
  //   default + restricted      ← unticked, lifting brings it back to default
  //   non-default + granted     ← ticked, unticking removes the grant
  //   non-default + not-granted ← unticked, ticking adds a grant
  function toggleAvailability(testKey) {
    const isDefault = defaults.has(testKey);
    if (isDefault) {
      // Toggle restriction
      setRestrictions(prev => restrictionSet.has(testKey)
        ? prev.filter(k => k !== testKey)
        : [...prev, testKey]);
    } else {
      // Toggle grant
      setGrants(prev => grantedSet.has(testKey)
        ? prev.filter(k => k !== testKey)
        : [...prev, testKey]);
      // If a non-default test was somehow restricted, clear that too.
      if (restrictionSet.has(testKey)) {
        setRestrictions(prev => prev.filter(k => k !== testKey));
      }
    }
  }

  if (library.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        This therapist's service group has no assessment library configured yet.
        Ask the admin to set one on the Service Group page first.
      </Alert>
    );
  }

  const effectiveCount = catalogue.filter(t =>
    (defaults.has(t.testKey) || grantedSet.has(t.testKey)) && !restrictionSet.has(t.testKey)
  ).length;

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Group default</strong> assessments are granted to every therapist in this clinic.
        Untick a default to <em>restrict</em> this therapist (e.g. not yet trained on it). Tick a
        non-default assessment to <em>grant</em> it to this therapist beyond the group default.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip size="small" label={`Library: ${library.length}`} variant="outlined" />
        <Chip size="small" label={`Group default: ${defaults.size}`} color="success" variant="outlined" />
        <Chip size="small" label={`Therapist add-on: ${grants.length}`} color="primary" variant="outlined" />
        <Chip size="small" label={`Restricted: ${restrictions.length}`} color="error" variant="outlined" />
        <Chip size="small" label={`Effective: ${effectiveCount}`} variant="outlined" />
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      <Stack divider={<Divider />} spacing={0}>
        {catalogue.map((t) => {
          const isDefault = defaults.has(t.testKey);
          const isRestricted = restrictionSet.has(t.testKey);
          const isGranted = grantedSet.has(t.testKey);
          const effective = (isDefault || isGranted) && !isRestricted;

          let badge = null;
          if (isDefault && isRestricted) badge = <Chip size="small" label="restricted" color="error" variant="outlined" />;
          else if (isDefault) badge = <Chip size="small" label="group default" color="success" variant="outlined" />;
          else if (isGranted) badge = <Chip size="small" label="granted" color="primary" variant="outlined" />;

          const action = isDefault
            ? (isRestricted ? 'Allow' : 'Allowed')
            : (isGranted ? 'Granted' : 'Grant');

          return (
            <Stack
              key={t.testKey}
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ md: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ py: 1 }}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle2" sx={isRestricted ? { textDecoration: 'line-through', opacity: 0.7 } : null}>
                    {t.shortName || t.testKey}
                    {t.fullName && t.fullName !== t.shortName && ` — ${t.fullName}`}
                  </Typography>
                  {badge}
                </Stack>
                {t.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t.description.slice(0, 160)}{t.description.length > 160 ? '…' : ''}
                  </Typography>
                )}
              </Box>
              <Tooltip
                title={isDefault
                  ? (isRestricted
                    ? 'Lift the restriction — restores the group default'
                    : 'Untick to restrict this therapist from administering this assessment')
                  : (isGranted
                    ? 'Untick to remove this grant'
                    : 'Tick to grant this therapist this assessment')}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={effective}
                      onChange={() => toggleAvailability(t.testKey)}
                    />
                  }
                  label={action}
                />
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={load} disabled={saving}>Reset</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
